"use client";

import { useState, useTransition } from "react";
import { saveCompanionFeedback } from "@/app/actions/companion-feedback";
import {
  normaliseGroundTruthSport,
  type CompanionSavedPlace,
} from "@/lib/companion/home-model";
import type { CompanionFeedbackKind } from "@/lib/companion/feedback";
import type { GroundTruthSport } from "@/lib/guardian/ground-truth-reports";
import { MtbGroundTruthPanel } from "@/app/_components/conditions/MtbGroundTruthPanel";
import { sportIsBeta, sportLabel } from "@/lib/conditions/sport-meta";
import { SportBetaBadge } from "@/app/_components/conditions/SportBetaBadge";

type Props = {
  place: CompanionSavedPlace | null;
  heading?: string;
  kicker?: string;
  isLoggedIn: boolean;
};

type FeedbackState =
  | { kind: "idle"; message: string }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

const FEEDBACK_BUTTONS: { kind: CompanionFeedbackKind; label: string }[] = [
  { kind: "accurate", label: "Accurate" },
  { kind: "wrong", label: "Wrong" },
  { kind: "better-than-forecast", label: "Better" },
  { kind: "worse-than-forecast", label: "Worse" },
  { kind: "icy", label: "Icy" },
  { kind: "poor", label: "Poor" },
];

const SCORE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function CompanionFeedbackPanel({
  place,
  heading,
  kicker,
  isLoggedIn,
}: Props) {
  const sport = place ? feedbackSportForPlace(place) : null;
  if (sport === "mtb" && place) {
    return (
      <MtbGroundTruthPanel
        locationSlug={place.slug ?? ""}
        locationName={place.name}
        estimatedCondition={null}
        windows={[]}
        isLoggedIn={isLoggedIn}
        prefill={place.groundTruthPrefill ?? null}
      />
    );
  }

  return (
    <LegacyCompanionFeedbackPanel
      place={place}
      heading={heading}
      kicker={kicker}
      sport={sport}
    />
  );
}

function LegacyCompanionFeedbackPanel({
  place,
  heading = "Ground truth",
  kicker = "Ground truth",
  sport,
}: Omit<Props, "isLoggedIn"> & {
  sport: GroundTruthSport | null;
}) {
  const enabled = Boolean(place?.slug && sport);
  const [status, setStatus] = useState<FeedbackState>({
    kind: "idle",
    message: enabled
      ? "No report sent in this session."
      : "Save a mountain or coast place to send ground-truth reports.",
  });
  const [observedScore, setObservedScore] = useState<number | null>(null);
  const [pendingKind, setPendingKind] = useState<CompanionFeedbackKind | null>(null);
  const [pending, startTransition] = useTransition();

  return (
    <section
      aria-labelledby="feedback-heading"
      className="rounded-md border border-border-light bg-white p-4 md:p-5"
    >
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
        {kicker}
      </p>
      <h2 id="feedback-heading" className="font-serif text-[26px] leading-tight text-navy">
        {heading}
      </h2>
      {place?.name ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] leading-relaxed text-text-muted">
          <p>
            Reporting for {place.name}{sport ? ` · ${sportLabel(sport)}` : ""}.
          </p>
          {sport && sportIsBeta(sport) ? <SportBetaBadge /> : null}
        </div>
      ) : null}
      <div className="mt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
            Score today, optional
          </p>
          {observedScore ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => setObservedScore(null)}
              className="min-h-[44px] text-[12px] font-semibold text-gold underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-55"
            >
              Clear
            </button>
          ) : null}
        </div>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {SCORE_OPTIONS.map((score) => {
            const selected = observedScore === score;
            return (
              <button
                key={score}
                type="button"
                aria-pressed={selected}
                aria-label={`Score ${score} out of 10`}
                disabled={!enabled || pending}
                onClick={() => setObservedScore(score)}
                className={`inline-flex min-h-[44px] items-center justify-center rounded-sm border px-2 py-2 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-55 ${
                  selected
                    ? "border-gold bg-gold text-navy"
                    : "border-border bg-warm-bg text-navy hover:border-gold/50 hover:bg-white"
                }`}
              >
                {score}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        {FEEDBACK_BUTTONS.map((button) => (
          <button
            key={button.kind}
            type="button"
            disabled={!enabled || pending}
            onClick={() => {
              if (!place?.slug || !sport) return;
              setPendingKind(button.kind);
              startTransition(async () => {
                const result = await saveCompanionFeedback({
                  locationSlug: place.slug ?? "",
                  sport,
                  kind: button.kind,
                  predictedScore: place.score,
                  predictedLabel: place.verdict,
                  observedScore,
                });
                setPendingKind(null);
                if (result.ok || ("reportSaved" in result && result.reportSaved)) {
                  setObservedScore(null);
                }
                setStatus(
                  result.ok
                    ? {
                        kind: "success",
                        message: successMessage(result.label, result.reportCount),
                      }
                    : "reportSaved" in result && result.reportSaved
                      ? {
                          kind: "error",
                          message: savedWithWarningMessage(result.error, result.reportCount),
                        }
                      : {
                          kind: "error",
                          message: result.error,
                      }
                );
              });
            }}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-warm-bg px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 hover:bg-white disabled:cursor-not-allowed disabled:opacity-55"
          >
            {pendingKind === button.kind ? "Sending" : button.label}
          </button>
        ))}
      </div>
      <p
        role={status.kind === "idle" ? undefined : "status"}
        className={`mt-3 text-[12.5px] leading-relaxed ${
          status.kind === "error"
            ? "text-rose-700"
            : status.kind === "success"
              ? "text-emerald-700"
              : "text-text-muted"
        }`}
      >
        {status.message}
      </p>
    </section>
  );
}

function feedbackSportForPlace(place: CompanionSavedPlace): GroundTruthSport | null {
  return (
    place.groundTruthSport ??
    normaliseGroundTruthSport(place.preferredSport) ??
    normaliseGroundTruthSport(place.locationType) ??
    normaliseGroundTruthSport(place.sport)
  );
}

function contributionMessage(reportCount: number | null | undefined): string | null {
  if (typeof reportCount !== "number") return null;
  return `You have ground-truthed ${reportCount} forecasts.`;
}

function successMessage(label: string, reportCount: number | null | undefined): string {
  return `${label}. ${contributionMessage(reportCount) ?? "Thanks for the ground truth."}`;
}

function savedWithWarningMessage(
  error: string,
  reportCount: number | null | undefined,
): string {
  const contribution = contributionMessage(reportCount);
  return contribution ? `${error} ${contribution}` : error;
}
